import {
  Wrench,
  Truck,
  BatteryCharging,
  CircleDot,
  Package,
  Siren,
  Phone,
} from "lucide-react";
import {
  SERVICE_IMG_ROADSIDE,
  SERVICE_IMG_TYRE,
  SERVICE_IMG_RECOVERY,
  ABOUT_IMG,
  HERO_IMG,
} from "../lib/constants";
import { CallButton, QuoteButton, SectionLabel } from "../components/Shared";

const SERVICES = [
  {
    icon: Wrench,
    title: "Roadside Assistance",
    desc: "For vehicles that will not start, warning lights, minor faults or sudden issues on the road.",
    img: SERVICE_IMG_ROADSIDE,
  },
  {
    icon: Truck,
    title: "Vehicle Recovery",
    desc: "Transport to your home, garage, workplace or chosen destination.",
    img: HERO_IMG,
  },
  {
    icon: BatteryCharging,
    title: "Jump Start Service",
    desc: "Battery flat? We can help get your vehicle started where possible.",
    img: SERVICE_IMG_ROADSIDE,
  },
  {
    icon: CircleDot,
    title: "Tyre Breakdown Support",
    desc: "Help with punctures, kerb damage, tyre blowouts and spare wheel fitting.",
    img: SERVICE_IMG_TYRE,
  },
  {
    icon: Package,
    title: "Van Recovery",
    desc: "Support for tradespeople, couriers and businesses using vans or light commercial vehicles.",
    img: ABOUT_IMG,
  },
  {
    icon: Siren,
    title: "Emergency Recovery",
    desc: "Fast-response support when your vehicle is blocking your plans, your work or your journey.",
    img: SERVICE_IMG_RECOVERY,
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* HERO */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="max-w-4xl">
            <SectionLabel>Our Services</SectionLabel>
            <h1
              data-testid="services-headline"
              className="mt-6 text-4xl sm:text-5xl lg:text-7xl font-display font-extrabold tracking-tighter leading-[0.95]"
            >
              Breakdown recovery services for
              <br />
              <span className="text-brand">
                cars, vans and light commercial vehicles.
              </span>
            </h1>
            <p className="mt-8 max-w-2xl text-base sm:text-lg text-white/70 leading-relaxed">
              From flat batteries to full vehicle recovery, ExpressBreakdown
              helps drivers get back on track with professional roadside and
              transport support.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <CallButton testId="services-call-btn" />
              <QuoteButton testId="services-quote-btn" />
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE LIST */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="space-y-px bg-white/10">
            {SERVICES.map((s, i) => (
              <div
                key={s.title}
                data-testid={`service-row-${i}`}
                className={`bg-black grid grid-cols-1 lg:grid-cols-12 gap-8 p-8 lg:p-12 items-center ${
                  i % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
              >
                <div
                  className={`lg:col-span-5 order-2 ${
                    i % 2 === 1 ? "lg:order-2" : "lg:order-1"
                  }`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden border border-white/10">
                    <img
                      src={s.img}
                      alt={s.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 bg-brand text-black text-xs font-bold uppercase tracking-wider px-3 py-1.5">
                      0{i + 1}
                    </div>
                  </div>
                </div>
                <div
                  className={`lg:col-span-7 order-1 ${
                    i % 2 === 1 ? "lg:order-1 lg:pr-8" : "lg:order-2 lg:pl-8"
                  }`}
                >
                  <div className="h-14 w-14 flex items-center justify-center border border-brand text-brand">
                    <s.icon className="h-6 w-6" strokeWidth={2.25} />
                  </div>
                  <h2 className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-display font-extrabold tracking-tight">
                    {s.title}
                  </h2>
                  <p className="mt-5 text-base sm:text-lg text-white/70 leading-relaxed max-w-xl">
                    {s.desc}
                  </p>
                  <a
                    href="tel:+447717759310"
                    data-testid={`service-call-${i}`}
                    className="mt-8 inline-flex items-center gap-3 text-sm font-bold uppercase tracking-wider text-brand hover:text-white transition-colors"
                  >
                    <Phone className="h-4 w-4" strokeWidth={2.5} />
                    Call for this service
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand text-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <h2 className="text-3xl sm:text-4xl lg:text-6xl font-display font-extrabold tracking-tighter">
                Not sure which service you need?
              </h2>
              <p className="mt-5 text-base sm:text-lg font-medium max-w-2xl">
                Call us and describe the situation. We'll advise the right
                recovery option and get moving.
              </p>
            </div>
            <div className="lg:col-span-4">
              <a
                href="tel:+447717759310"
                data-testid="services-final-call"
                className="w-full inline-flex items-center justify-center gap-3 bg-black text-brand font-bold uppercase tracking-wider text-base px-7 py-5"
              >
                <Phone className="h-5 w-5" strokeWidth={2.75} />
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
